import type {
  ComponentPropsWithRef,
  FocusEventHandler,
} from "react"
import {
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react"

interface InputProps extends ComponentPropsWithRef<"input"> {
  value: number;
  defaultValue?: number | string;
}

type MultiInputProps = [InputProps, InputProps]

const lowSliderValueType = "low"
const highSliderValueType = "high"
const sliderValueTypeKey = "slidervaluetype"

/*
 * this hook takes two number input props and returns them in a tuple sorted by value
 * the tuple is not re-sorted while the inputs are being edited
 */
export function useSortedSliderValueInputProps(
  // the input props that should be sorted
  inputOneProps: InputProps,
  inputTwoProps: InputProps,
): MultiInputProps {

  // which input (high or low) is currently focused
  const [
    sliderValueInputFocused,
    setSliderValueInputFocused,
  ] = useState<string | null>(null)

  const onFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setSliderValueInputFocused(e.target.dataset[sliderValueTypeKey] || null)
    },
    [],
  )

  const onBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    () => {
      setSliderValueInputFocused(null)
    },
    [],
  )

  const sortedInputPropsRef = useRef<MultiInputProps>([
    inputOneProps,
    inputTwoProps,
  ])

  const sortedInputProps = useMemo(
    () => {
      let lowInputProps = sortedInputPropsRef.current[0]
      let highInputProps = sortedInputPropsRef.current[1]

      // prevent re-sorting the values while the user still has the input focused
      if (sliderValueInputFocused === lowSliderValueType) {
        // get either inputOne or inputTwo, whichever currently has the "low" value
        const newValue = inputOneProps.value === highInputProps.value
          ? inputTwoProps
          : inputOneProps

        lowInputProps = newValue
      } else if (sliderValueInputFocused === highSliderValueType) {
        // get either inputOne or inputTwo, whichever currently has the "high" value
        const newValue = inputOneProps.value === lowInputProps.value
          ? inputTwoProps
          : inputOneProps

        highInputProps = newValue
      } else {
        // get either inputOne or inputTwo, whichever currently has the "low" value
        lowInputProps = inputOneProps.value <= inputTwoProps.value
          ? inputOneProps
          : inputTwoProps

        // get either inputOne or inputTwo, whichever currently has the "high" value
        highInputProps = lowInputProps === inputOneProps
          ? inputTwoProps
          : inputOneProps
      }

      lowInputProps = {
        ...lowInputProps,
        [`data-${sliderValueTypeKey}`]: lowSliderValueType,
        onBlur,
        onFocus,
      }

      highInputProps = {
        ...highInputProps,
        [`data-${sliderValueTypeKey}`]: highSliderValueType,
        onBlur,
        onFocus,
      }

      sortedInputPropsRef.current = [
        lowInputProps,
        highInputProps,
      ]

      return sortedInputPropsRef.current
    },
    [
      inputOneProps,
      inputTwoProps,
      onBlur,
      onFocus,
      sliderValueInputFocused,
      sortedInputPropsRef,
    ],
  )

  return sortedInputProps
}
